import type { ResponseError } from "superagent";

export interface BackpackResponseErrorBody {
  error: {
    message: string;
    code?: number;
    internal_code?: string;
  };
}

// Start superagent specific handling
interface BackpackSuperagentResponseError extends ResponseError {
  response: {
    body: BackpackResponseErrorBody;
  } & ResponseError["response"];
}
function isBackpackSuperagentResponseError(
  e: any
): e is BackpackSuperagentResponseError {
  return Boolean(e?.response?.body?.error?.message);
}
export function throwBackpackSuperagentResponseError(e: any) {
  if (isBackpackSuperagentResponseError(e)) {
    // If the error is from the server, make a new error with the message from the response
    throw new Error(e.response.body.error.message);
  }

  // Otherwise, we don't know how to handle this error
  throw e;
}

// End superagent specific handling

// These can be used when handling fetch errors
export const isBackpackResponseErrorBody = (
  e: any
): e is BackpackResponseErrorBody => typeof e?.error?.message === "string";
export class BackpackServiceError extends Error {
  code: number;
  internalCode?: string;

  constructor(message: BackpackResponseErrorBody) {
    super(message.error.message);
    this.name = "BackpackServiceError";
    this.code = message.error.code ?? 0;
    this.internalCode = message.error.internal_code;
  }
}
export function throwBackpackResponseError(e: any) {
  if (isBackpackResponseErrorBody(e)) {
    // If the error is from the server, make a new error with the message from the response
    throw new BackpackServiceError(e);
  }

  // Otherwise, we don't know how to handle this error
  throw e;
}
/**
 * Rocket (rust server library) and Nginx return a block of HTML as default error responses.
 * This function parses the error status in the <title> tag and returns that string
 */
export function parseHtmlErrorMessage(message?: string | null) {
  if (!message) {
    return "Something went wrong. Please contact Backpack support if the problem persists.";
  }
  if (message.includes("<html")) {
    return `Error: ${message
      .split("<title>")
      .slice(1)
      .join("")
      .split("</title>")
      .slice(0, 1)
      .join("")
      .trim()}`;
  }
  return message;
}
